import saveAs from 'file-saver';

import { BorrowerSpread, BorrowerSpreadFile, useLazyQueryBorrowerSpreadAuditDownload } from '@oaknorth/api-polaris';
import {
  Box,
  Button,
  Icon,
  MdCheckCircleOutline,
  MdErrorOutline,
  MdFileDownload,
  Status,
  Table,
  toaster,
  VStack,
} from '@oaknorth/component-core-v2';
import {
  Cell,
  createColumnHelper,
  Header,
  TableBody,
  TableCell,
  TableHeader,
  TablePopoverCell,
  useSimpleTable,
} from '@oaknorth/component-table';
import { T, useTranslation } from '@oaknorth/util-intl';

interface BorrowerSpreadAuditProps {
  file: BorrowerSpreadFile;
  borrowerId: string;
  borrowerName?: string;
}

const columnHelper = createColumnHelper<BorrowerSpread>();

const popover = (cell: Cell<BorrowerSpread, string | null>): React.ReactNode => {
  const value = cell.getValue();
  if (value == null) {
    return '-';
  }

  const reasons = value.split(',').map((reason) => reason.charAt(0).toUpperCase() + reason.slice(1));
  if (reasons.length === 1) return reasons[0];

  const popoverTitle = T('explainability.spreadAuditTable.reasons', { reason: reasons[0], amount: reasons.length - 1 });
  return (
    <TablePopoverCell popoverTitle={popoverTitle} border="0" cellContentsProps={{ padding: '0' }}>
      {reasons.map((reason) => (
        <Box key={reason}>{reason}</Box>
      ))}
    </TablePopoverCell>
  );
};

const columns = [
  columnHelper.accessor('valid', {
    id: 'valid',
    header: () => T`explainability.spreadAuditTable.valid`,
    size: 20,
    meta: {
      tableHeader: (props) => <Header {...props} width="5%" textAlign="center" />,
      tableCell: ({ cell, ...props }) => (
        <TableCell cell={cell} {...props}>
          {cell.getValue() ? (
            <Icon as={MdCheckCircleOutline} color="on-lime.500" boxSize={6} />
          ) : (
            <Icon as={MdErrorOutline} color="red.500" boxSize={6} />
          )}
        </TableCell>
      ),
    },
  }),
  columnHelper.accessor('source_row_index', {
    id: 'source_row_index',
    header: () => T`explainability.spreadAuditTable.rowIndex`,
    cell: ({ cell }) => cell.getValue(),
  }),
  columnHelper.accessor('id', {
    id: 'id',
    header: () => T`explainability.spreadAuditTable.spreadId`,
    cell: ({ cell }) => cell.getValue(),
  }),
  columnHelper.accessor('exclusion_reasons', {
    id: 'exclusion_reasons',
    header: () => T`explainability.spreadAuditTable.exclusions`,
    cell: ({ cell }) => popover(cell),
  }),
  columnHelper.accessor('warning_reasons', {
    id: 'warning_reasons',
    header: () => T`explainability.spreadAuditTable.warnings`,
    cell: ({ cell }) => popover(cell),
  }),
];

const CSV_TYPE_FILE = 'text/csv;charset=utf-8';

export function BorrowerSpreadAuditTable({ file, borrowerId, borrowerName }: BorrowerSpreadAuditProps) {
  const table = useSimpleTable({ columns, rowData: file.spreads });
  const { t } = useTranslation();

  // eslint-disable-next-line @oaknorth/ui/no-template-literals -- filename, not part of UI
  const fileName = file.original_filename.replace(/\.csv/, `_${borrowerName ?? borrowerId}.csv`);

  const downloadQuery = useLazyQueryBorrowerSpreadAuditDownload();

  const downloadSpreadAuditFile = async () => {
    try {
      const data = await downloadQuery.executeAsync({
        path: { inputFileId: file.input_file_id, legalEntityId: borrowerId },
      });

      const csvFile = new Blob([data], { type: CSV_TYPE_FILE });

      saveAs(csvFile, fileName);

      toaster.create({
        type: Status.SUCCESS,
        title: t`explainability.spreadAuditTable.download.success`,
      });
    } catch (_error) {
      toaster.create({ type: Status.ERROR, title: t`explainability.spreadAuditTable.download.error` });
    }
  };

  return (
    <VStack alignItems="flex-start" gap={2} width="100%" backgroundColor="#FFFFFF" border="1px" borderColor="gray.200">
      <Box padding={5}>
        <Button
          loading={!borrowerName}
          leftIcon={<MdFileDownload />}
          aria-label={t`explainability.spreadAuditTable.download`}
          onClick={downloadSpreadAuditFile}
        >
          {fileName}
        </Button>
      </Box>
      <Table data-testid="borrower-spread-audit-table">
        <TableHeader table={table} />
        <TableBody table={table} />
      </Table>
    </VStack>
  );
}
